import React from 'react';
import { Button, ButtonProps, CircularProgress, styled, Theme, useTheme } from '@mui/material';
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom';
import { TakeDefined } from '@types';
import { merge } from 'lodash';
import { Props } from './button.types';

const LwButton = ({
  iconVariant = 'outlined',
  iconColorMode = 'stroke',
  sx,
  startIcon,
  disabled,
  isLoading,
  ...props
}: Props & { isLoading?: boolean }) => {
  const theme = useTheme();
  const sxOverride = getSxOverride({ iconVariant, iconColorMode }, theme);

  const computedStartIcon = isLoading ? <CircularProgress size={18} color="inherit" /> : startIcon;

  return (
    <StyledButton
      LinkComponent={LinkComponent}
      sx={merge({}, sx, sxOverride)}
      variant="contained"
      startIcon={computedStartIcon}
      disabled={disabled || isLoading}
      {...props}
    />
  );
};

const LinkComponent = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }
>((props, ref) => {
  const { href, ...other } = props;
  return <RouterLink ref={ref} to={href} {...other} role="link" />;
});

const StyledButton = styled(Button)<ButtonProps>(({ theme }) => ({
  '&.MuiButton-root': {
    alignItems: 'center',
    boxShadow: 'none',
    display: 'flex',
    fontFamily: 'Urbanist',
    fontWeight: 'bold',
    justifyContent: 'center',
    textTransform: 'none',

    '&.Mui-disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
  },

  '&.MuiButton-containedPrimary': {
    backgroundColor: theme.palette.lwPrimaryAccentLight[100],
    borderRadius: theme.spacing(7),
    color: theme.palette.lwWhite[100],
    fontSize: '16px',
    fontWeight: 700,
    fontVariationSettings: '"wght" 700',
    gap: theme.spacing(2),
    lineHeight: theme.spacing(5),
    height: '56px',
    padding: theme.spacing(4, 6),

    '&:hover': {
      backgroundColor: theme.palette.lwPrimaryAccentLight[100],
      boxShadow: `0px 12px 32px ${theme.palette.lwPrimaryAccentDark[40]}`,
      color: theme.palette.lwWhite[100],
    },

    '&:focus': {
      boxShadow: `0px 0px 0px 4px ${theme.palette.lwPrimaryAccentDark[10]}`,
    },

    '&:active': {
      backgroundColor: theme.palette.lwPrimaryAccentDark[100],
      color: theme.palette.lwWhite[80],
    },

    '&.Mui-disabled': {
      backgroundColor: theme.palette.lwPrimaryAccentLight[20],
      color: theme.palette.lwWhite[80],
    },
  },

  '&.MuiButton-containedSecondary': {
    backgroundColor: theme.palette.lwWhite[100],
    border: `1px solid ${theme.palette.lwPrimaryAccentDark[20]}`,
    borderRadius: theme.spacing(7),
    color: theme.palette.lwPrimaryAccentDark[100],
    fontSize: '16px',
    fontWeight: 600,
    fontVariationSettings: '"wght" 600',
    gap: theme.spacing(2),
    height: '56px',
    lineHeight: theme.spacing(5),
    padding: theme.spacing(4, 6),

    '&:hover': {
      backgroundColor: theme.palette.lwWhite[100],
      border: `1px solid ${theme.palette.lwPrimaryAccentDark[40]}`,
      boxShadow: `0px 8px 24px ${theme.palette.lwPrimaryAccentDark[20]}`,
      color: theme.palette.lwPrimaryAccentLight[100],
    },

    '&:focus': {
      boxShadow: `0px 0px 0px 4px ${theme.palette.lwPrimaryAccentLight[10]}`,
    },

    '&:active': {
      backgroundColor: theme.palette.lwPrimaryAccentDark[10],
      border: `1px solid currentColor`,
      color: theme.palette.lwPrimaryAccentDark[100],
    },

    '&.Mui-disabled': {
      backgroundColor: theme.palette.lwWhite[100],
      color: theme.palette.lwPrimaryAccentDark[40],
    },
  },

  '&.MuiButton-containedTertiary': {
    border: `1px solid ${theme.palette.lwPrimaryAccentDark[40]}`,
    borderRadius: theme.spacing(6),
    color: theme.palette.lwPrimaryAccentDark[100],
    fontSize: '14px',
    fontWeight: 600,
    fontVariationSettings: '"wght" 500',
    lineHeight: theme.spacing(5),
    minHeight: '32px',
    padding: theme.spacing(2, 3),
    letterSpacing: 0,

    '&:hover': {
      boxShadow: `0px 6px 16px ${theme.palette.lwPrimaryAccentDark[20]}`,
    },

    '&:focus': {
      border: `1px solid ${theme.palette.lwPrimaryAccentDark[40]}`,
      boxShadow: `0px 0px 0px 4px ${theme.palette.lwPrimaryAccentLight[10]}`,
      backgroundColor: theme.palette.lwBlack[2],
    },

    '&:active': {
      backgroundColor: theme.palette.lwPrimaryAccentDark[10],
      border: `1px solid ${theme.palette.lwPrimaryAccentDark[80]}`,
      color: theme.palette.lwPrimaryAccentDark[100],
    },

    '&.Mui-disabled': {
      background: theme.palette.lwState.disabled,
      border: `1px solid ${theme.palette.lwPrimaryAccentDark[10]}`,
      color: theme.palette.lwPrimaryAccentDark[40],
    },

    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1),
    },

    '& .MuiButton-endIcon': {
      marginLeft: theme.spacing(1),
    },
  },
}));

const getSxOverride = (
  {
    iconVariant,
    iconColorMode,
  }: {
    iconVariant: TakeDefined<Props['iconVariant']>;
    iconColorMode: TakeDefined<Props['iconColorMode']>;
  },
  theme: Theme
) => ({
  '&.MuiButton-root': {
    ...(iconVariant === 'filled'
      ? {
          padding: theme.spacing(4),
        }
      : {}),
  },
  '&.MuiButton-containedPrimary svg path': {
    [iconColorMode]: 'currentColor',
  },
  '&.MuiButton-containedSecondary': {
    '& svg path, &:hover svg path, &:active svg path': {
      [iconColorMode]: 'currentColor',
    },
  },
  '&.MuiButton-containedTertiary': {
    '& svg path, &:hover svg path, &:active svg path': {
      [iconColorMode]: 'currentColor',
    },
  },
  '&:active .MuiButton-startIcon svg path, &:active .MuiButton-endIcon svg path': {
    ...(iconVariant === 'filled'
      ? {
          fill: theme.palette.lwPrimaryAccentDark[100],
        }
      : {}),
  },
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    ...(iconVariant === 'filled'
      ? {
          alignItems: 'center',
          backgroundColor: 'currentColor',
          borderRadius: '50%',
          display: 'flex',
          height: '40px',
          justifyContent: 'center',
          width: '40px',

          '& svg path': {
            fill: theme.palette.lwPrimaryAccentLight[100],
          },
        }
      : {}),
  },
});

export { LwButton };
